export type PeerRole = "receiver" | "sender";

export interface RoomCredentials {
  roomId: string;
  secret: string;
}

export interface EncryptedEnvelope {
  readonly v: 1;
  readonly iv: string;
  readonly data: string;
}

export type SignalPayload =
  | { readonly type: "offer"; readonly sdp: RTCSessionDescriptionInit }
  | { readonly type: "answer"; readonly sdp: RTCSessionDescriptionInit }
  | { readonly type: "candidate"; readonly candidate: RTCIceCandidateInit }
  | { readonly type: "restart" }
  | { readonly type: "bye" };

export interface FileOffer {
  readonly id: string;
  readonly name: string;
  readonly size: number;
  readonly type: string;
  readonly lastModified: number;
  /** Present on legacy manifests; current senders provide the digest with file-end. */
  readonly sha256?: string;
  readonly thumbnail?: string;
}

export interface TransferManifest {
  readonly transferId: string;
  readonly files: readonly FileOffer[];
  readonly totalSize: number;
}

export type CollisionAction = "replace" | "rename" | "skip";

export interface FileDecision {
  readonly id: string;
  readonly action: CollisionAction;
  readonly finalName: string;
}

export type TransferControlMessage =
  | { readonly type: "manifest"; readonly manifest: TransferManifest }
  | { readonly type: "decision"; readonly decisions: readonly FileDecision[] }
  | { readonly type: "file-start"; readonly fileId: string; readonly offset: number }
  | { readonly type: "file-end"; readonly fileId: string; readonly sha256?: string }
  | { readonly type: "ack"; readonly fileId: string; readonly offset: number }
  | { readonly type: "file-complete"; readonly fileId: string }
  | { readonly type: "resume"; readonly offsets: Readonly<Record<string, number>> }
  | { readonly type: "transfer-complete" }
  | { readonly type: "cancel"; readonly reason?: string }
  | { readonly type: "error"; readonly code: string; readonly message: string };

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export function randomBase64Url(byteLength: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(byteLength));
  return bytesToBase64Url(bytes);
}

export function createRoomCredentials(): RoomCredentials {
  return { roomId: randomBase64Url(16), secret: randomBase64Url(32) };
}

export function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

export function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = value.replaceAll("-", "+").replaceAll("_", "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

async function deriveSignalKey(secret: string, roomId: string): Promise<CryptoKey> {
  const source = await crypto.subtle.importKey("raw", base64UrlToBytes(secret), "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: encoder.encode(roomId),
      info: encoder.encode("kitae-transfer/signaling/v1"),
    },
    source,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

export class SignalCipher {
  readonly #roomId: string;
  readonly #key: Promise<CryptoKey>;
  readonly #prefix = crypto.getRandomValues(new Uint8Array(4));
  readonly #seen = new Set<string>();
  #counter = 0n;

  constructor(roomId: string, secret: string) {
    this.#roomId = roomId;
    this.#key = deriveSignalKey(secret, roomId);
  }

  async encrypt(payload: SignalPayload): Promise<EncryptedEnvelope> {
    const iv = new Uint8Array(12);
    iv.set(this.#prefix);
    new DataView(iv.buffer).setBigUint64(4, this.#counter, false);
    this.#counter += 1n;
    const data = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv, additionalData: encoder.encode(this.#roomId) },
      await this.#key,
      encoder.encode(JSON.stringify(payload)),
    );
    return { v: 1, iv: bytesToBase64Url(iv), data: bytesToBase64Url(new Uint8Array(data)) };
  }

  async decrypt(envelope: EncryptedEnvelope): Promise<SignalPayload> {
    if (envelope.v !== 1 || this.#seen.has(envelope.iv)) throw new Error("Enveloppe de signalisation invalide ou rejouée");
    const iv = base64UrlToBytes(envelope.iv);
    if (iv.byteLength !== 12) throw new Error("Vecteur d'initialisation invalide");
    const clear = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv, additionalData: encoder.encode(this.#roomId) },
      await this.#key,
      base64UrlToBytes(envelope.data),
    );
    this.#seen.add(envelope.iv);
    if (this.#seen.size > 2_048) this.#seen.delete(this.#seen.values().next().value!);
    return JSON.parse(decoder.decode(clear)) as SignalPayload;
  }
}

export function parseControlMessage(value: string): TransferControlMessage {
  const parsed = JSON.parse(value) as { type?: unknown };
  if (!parsed || typeof parsed !== "object" || typeof parsed.type !== "string") throw new Error("Message de transfert invalide");
  return parsed as TransferControlMessage;
}

export function formatBytes(value: number): string {
  if (value < 1_000) return `${value} o`;
  const units = ["ko", "Mo", "Go", "To"];
  let amount = value;
  let index = -1;
  do {
    amount /= 1_000;
    index += 1;
  } while (amount >= 1_000 && index < units.length - 1);
  return `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: amount < 10 ? 1 : 0 }).format(amount)} ${units[index]}`;
}

export function safeFileName(value: string): string {
  const cleaned = value.normalize("NFC").replace(/[\\/:*?"<>\u0000-\u001f|]/gu, "_").replace(/[. ]+$/u, "").slice(0, 240);
  return cleaned || "fichier";
}
